import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect } from "react";
import { Pressable, ScrollView } from "react-native";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { CardEntryErrorBanner } from "@/features/Square/CardEntryErrorBanner";
import { useBookingPaymentCards } from "@/features/Square/hooks/useBookingPaymentCards/useBookingPaymentCards";
import { Button } from "@whoosh/design-system/src/components/controls";
import {
  Loader,
  SkeletonLoader,
} from "@whoosh/design-system/src/components/display";
import {
  CheckIcon,
  PlusIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { SquareCard } from "@whoosh/features/src/Square";

import { BookingPaymentMethodSelectModalQuery } from "./__generated__/BookingPaymentMethodSelectModalQuery.graphql";

type BookingPaymentMethodSelectModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  selectedPaymentMethodId?: string | null;
  setSelectedPaymentMethodId: (id: string) => void;
  openSquareCardEntryModal: () => void;
  bookingDate: string;
  onNoEligibleCards?: () => void;
  errorMessage?: string;
  loading?: boolean;
};

export const BookingPaymentMethodSelectModal = (
  props: BookingPaymentMethodSelectModalProps
) => {
  return (
    <StandardModal
      isOpen={props.isOpen}
      onClose={() => props.setIsOpen(false)}
      disableContentScroll={true}
      upperTitle={t`Payment Methods`}
      hideCloseButton={props.loading}
      content={
        <Box flex={1}>
          <Box px={6}>
            <CardEntryErrorBanner message={props.errorMessage} />
          </Box>
          {props.loading ? (
            <Loader />
          ) : props.isOpen ? (
            <PaymentMethodSelectQuerrier {...props} />
          ) : null}
        </Box>
      }
    />
  );
};

const PaymentMethodSelectQuerrier = ({
  bookingDate,
  isOpen,
  ...props
}: BookingPaymentMethodSelectModalProps) => {
  const [queryRef, loadQuery] =
    useQueryLoader<BookingPaymentMethodSelectModalQuery>(
      BookingPaymentMethodSelectModalQueryString
    );
  useEffect(() => {
    if (!isOpen) return;
    loadQuery({ bookingDate }, { fetchPolicy: "network-only" });
  }, [bookingDate, loadQuery, isOpen]);

  return (
    <Suspense
      fallback={
        <VStack gap={4} paddingHorizontal={6}>
          <SkeletonLoader height={34} width={300} borderRadius="2" />
          <SkeletonLoader height={34} width={300} borderRadius="2" />
          <SkeletonLoader height={34} width={300} borderRadius="2" />
        </VStack>
      }
    >
      {queryRef && queryRef.variables.bookingDate === bookingDate ? (
        <PaymentMethodSelectInner
          queryRef={queryRef}
          bookingDate={bookingDate}
          isOpen={isOpen}
          {...props}
        />
      ) : null}
    </Suspense>
  );
};

const PaymentMethodSelectInner = ({
  selectedPaymentMethodId,
  setSelectedPaymentMethodId,
  openSquareCardEntryModal,
  queryRef,
  bookingDate,
  isOpen,
  onNoEligibleCards,
}: {
  queryRef: PreloadedQuery<BookingPaymentMethodSelectModalQuery>;
} & BookingPaymentMethodSelectModalProps) => {
  const data = usePreloadedQuery(
    BookingPaymentMethodSelectModalQueryString,
    queryRef
  );

  const cards = useBookingPaymentCards(data.session, bookingDate);

  useEffect(() => {
    if (isOpen && cards.length === 0) onNoEligibleCards?.();
  }, [isOpen, cards.length, onNoEligibleCards]);

  return (
    <Box flex={1} px={6} pb={6}>
      <Box my={3}>
        <ScrollView>
          {cards.map((card, index) => (
            <SquareCard
              key={index}
              fragmentRef={card}
              onPress={() => setSelectedPaymentMethodId(card.id)}
              rightIcon={
                selectedPaymentMethodId === card.id ? <CheckIcon /> : null
              }
            />
          ))}
        </ScrollView>
      </Box>

      {!cards.length ? (
        <Button
          variant="main"
          onPress={openSquareCardEntryModal}
          accessibilityLabel={t`Add card again`}
        >{t`Add card`}</Button>
      ) : (
        <Pressable
          onPress={openSquareCardEntryModal}
          accessibilityRole="button"
          accessibilityLabel={t`Add New Credit Card`}
        >
          <HStack gap={2} alignItems="center">
            <Box
              bg="surface-secondary"
              width={40}
              height={26}
              borderRadius="2"
              alignItems="center"
              justifyContent="center"
            >
              <PlusIcon width={16} height={16} color="text-main-primary" />
            </Box>
            <Text color="text-main-primary">{t`Add New Credit Card`}</Text>
          </HStack>
        </Pressable>
      )}
    </Box>
  );
};

const BookingPaymentMethodSelectModalQueryString = graphql`
  query BookingPaymentMethodSelectModalQuery($bookingDate: Date!) {
    session {
      ...useBookingPaymentCards_session @arguments(bookingDate: $bookingDate)
    }
  }
`;
